import { AxiosResponse } from 'axios';

import { IOutputChannelPayload } from 'components/OutputChannelSidebar/types';
import { normalizeBaseGist, normalizePageData } from 'utils/commonNormalizer';
import { normalizeSearchFiltersPayload } from 'utils/filters';
import { prepareFormData } from 'utils/form';
import { IAllPageFilters, IBaseGist, IPageType } from 'utils/types';

import { analyticsInstance } from '../base';

import {
  normalizeNotification,
  normalizeNotificationChannel,
  normalizeTemplate,
} from './normalizers';
import {
  INotification,
  INotificationFormData,
  INotificationOutputChannel,
  INotificationOutputChannelsData,
  ITemplate,
  ITemplateData,
  ITemplateFormData,
  IVerifyOutputChannelPayload,
  TNotificationsData,
} from './types';

const notificationAPI = {
  async getAllOutputChannels(
    filters: IAllPageFilters,
  ): Promise<IPageType<INotificationOutputChannel>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await analyticsInstance.get('channels', {
      params: normalizedFilter,
    });

    return normalizePageData<INotificationOutputChannelsData, INotificationOutputChannel>(
      response.data,
      normalizeNotificationChannel,
      JSON.stringify(filters.search),
    );
  },

  async getOutputChannel(id: number): Promise<INotificationOutputChannel> {
    const response = await analyticsInstance.get(`channels/${id}`);
    return normalizeNotificationChannel(response.data);
  },

  async createOutputChannel({ payload }: IOutputChannelPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await analyticsInstance.post(`channels`, normalizedPayload);

    return normalizeBaseGist(response?.data);
  },

  async editOutputChannel({ id, payload }: IOutputChannelPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await analyticsInstance.put(`channels/${id}`, normalizedPayload);

    return normalizeBaseGist(response?.data);
  },

  async verifyOutputChannel({
    id,
    otp,
  }: IVerifyOutputChannelPayload): Promise<IBaseGist> {
    const response = await analyticsInstance.put(`channels/${id}/confirmation/${otp}`);

    return normalizeBaseGist(response?.data);
  },

  async deleteOutputChannel(id: number | string): Promise<AxiosResponse> {
    const result = await analyticsInstance.delete(`channels/${id}`);

    return result;
  },

  async resendOtp(id: number | string): Promise<AxiosResponse> {
    const result = await analyticsInstance.post(`channels/${id}/confirmation`);

    return result;
  },

  async getAllNotifications(filters: IAllPageFilters): Promise<IPageType<INotification>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await analyticsInstance.get('notifications', {
      params: normalizedFilter,
    });

    return normalizePageData<TNotificationsData, INotification>(
      response.data,
      normalizeNotification,
      JSON.stringify(filters.search),
    );
  },

  async getNotification(id: number | string): Promise<INotification> {
    const response = await analyticsInstance.get(`notifications/${id}`);
    return normalizeNotification(response.data);
  },

  async createNotification({ payload }: INotificationFormData): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await analyticsInstance.post(`notifications`, normalizedPayload);

    return normalizeBaseGist(response?.data);
  },

  async editNotification({ id, payload }: INotificationFormData): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await analyticsInstance.put(
      `notifications/${id}`,
      normalizedPayload,
    );

    return normalizeBaseGist(response?.data);
  },

  async deleteNotification(id: number | string): Promise<AxiosResponse> {
    const result = await analyticsInstance.delete(`notifications/${id}`);

    return result;
  },

  async getAllTemplates(filters: IAllPageFilters): Promise<IPageType<ITemplate>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await analyticsInstance.get('templates', {
      params: normalizedFilter,
    });

    return normalizePageData<ITemplateData, ITemplate>(
      response.data,
      normalizeTemplate,
      JSON.stringify(filters.search),
    );
  },

  async getTemplate(id: number | string): Promise<ITemplate> {
    const response = await analyticsInstance.get(`templates/${id}`);
    return normalizeTemplate(response.data);
  },

  async createTemplate({ payload }: ITemplateFormData): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await analyticsInstance.post(`templates`, normalizedPayload);

    return normalizeBaseGist(response?.data);
  },

  async editTemplate({ id, payload }: ITemplateFormData): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await analyticsInstance.put(`templates/${id}`, normalizedPayload);

    return normalizeBaseGist(response?.data);
  },

  async deleteTemplate(id: number | string): Promise<AxiosResponse> {
    const result = await analyticsInstance.delete(`templates/${id}`);

    return result;
  },

  async calculateNotification(id: number | string): Promise<AxiosResponse> {
    const result = await analyticsInstance.post(`notifications/${id}/calculation`);

    return result;
  },
};

export default notificationAPI;
